import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Copy, Share2, Wallet, TrendingUp, ExternalLink, Loader2, Check, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import ShareButtons from './ShareButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * What-pays-you preview rows, keyed by role.
 *
 * Each entry lists the most logically-applicable paid products for that
 * persona. We show GBP headline prices (matching the rest of the site)
 * and compute the user's actual cut from `commission_pct`. The point is
 * to make the affiliate offer feel concrete: "share this and earn £X".
 */
const ROLE_PRODUCTS = {
  agent: [
    { label: 'Agent City Subscription', price: 99, cadence: '/mo', note: 'every paying agent you refer' },
    { label: 'Pulse Invest Report', price: 49, cadence: 'one-off', note: 'each report a referral buys' },
    { label: 'Pro Contact Unlock pack', price: 4.99, cadence: 'each', note: 'every contact unlock' },
  ],
  professional: [
    { label: 'Pro City Subscription', price: 49, cadence: '/mo', note: 'every Pro who joins via your link' },
    { label: 'Pulse Invest Report', price: 49, cadence: 'one-off', note: 'each report a referral buys' },
    { label: 'Pro Contact Unlock pack', price: 4.99, cadence: 'each', note: 'every contact unlock' },
  ],
  seller: [
    { label: 'Pulse Listing Boost', price: 99, cadence: 'one-off', note: 'every boost a referred seller buys' },
    { label: 'Pulse Invest Report', price: 49, cadence: 'one-off', note: 'each report a referral buys' },
    { label: 'Pro City Subscription', price: 49, cadence: '/mo', note: 'agents/solicitors you refer' },
  ],
  buyer: [
    { label: 'Pulse Invest Report', price: 49, cadence: 'one-off', note: 'each report a referral buys' },
    { label: 'Buyer Pulse Subscription', price: 29, cadence: '/mo', note: 'every buyer who upgrades' },
    { label: 'Pro Contact Unlock', price: 4.99, cadence: 'each', note: 'every contact unlock' },
  ],
  tenant: [
    { label: 'Pulse Invest Report', price: 49, cadence: 'one-off', note: 'each report a referral buys' },
    { label: 'Buyer Pulse Subscription', price: 29, cadence: '/mo', note: 'every renter who upgrades' },
    { label: 'Pro Contact Unlock', price: 4.99, cadence: 'each', note: 'every letting-agent contact' },
  ],
};
const ROLE_TAGLINE = {
  agent: 'Top-tier 25% commission — the highest rate on the network. Every agent you bring in pays you monthly while they remain a subscriber.',
  professional: 'Premium 20% commission on every Pro subscription, contact unlock and Pulse Report your referrals buy.',
  seller: 'Earn 15% on every paid product your referrals buy — Pulse Reports, Listing Boosts, agent subscriptions, all of it.',
  buyer: 'Earn 15% every time someone you refer buys a Pulse Report, upgrades to Pulse Subscription, or unlocks a verified-pro contact.',
  tenant: 'Refer fellow renters — you earn 15% on every Pulse Report, subscription and letting-agent contact unlock they pay for.',
};

/**
 * Affiliate Widget — every dashboard's growth card.
 *
 * Shows the user's share link, current balance per currency, lifetime
 * earned, commission tier badge, and recent credit history. One-click
 * copy + inline social share row (Facebook/WA/X/LinkedIn/Telegram/Copy)
 * with the share URL auto-pointing to `https://shopperden.com/r/{code}`.
 *
 * Hides gracefully when the user isn't loaded yet or has no referral
 * code (shouldn't happen — every user gets a code on signup, but defensive).
 */
const AffiliateWidget = ({ dataTestId = 'affiliate-widget' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectAvailable, setConnectAvailable] = useState(null); // null = unknown, true/false once probed
  const [connecting, setConnecting] = useState(false);

  const refresh = async () => {
    try {
      const [r, s, a] = await Promise.all([
        axios.get(`${API_URL}/api/affiliate/me`),
        axios.get(`${API_URL}/api/affiliate/connect/status`).catch(() => ({ data: { connected: false } })),
        axios.get(`${API_URL}/api/affiliate/connect/availability`).catch(() => ({ data: { available: false } })),
      ]);
      setData(r.data);
      setConnectStatus(s.data);
      setConnectAvailable(!!a.data?.available);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const r = await axios.post(`${API_URL}/api/affiliate/connect/onboard-link`, {
        country: 'UK',
        origin: window.location.origin,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;  // Stripe-hosted onboarding
      } else {
        toast.error('Could not start Stripe onboarding');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Structured 503 from backend → degrade gracefully, don't bounce
      if (detail && typeof detail === 'object' && detail.code === 'stripe_connect_unavailable') {
        setConnectAvailable(false);
        toast.info(detail.message || 'Instant payouts are temporarily disabled — manual payouts active.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Stripe Connect setup failed');
      }
    } finally {
      setConnecting(false);
    }
  };

  if (loading || !data?.referral_code) return null;

  const shareUrl = `https://shopperden.com/r/${data.referral_code}`;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Affiliate link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Copy failed'); }
  };

  const handlePayout = async (ccy) => {
    setRequesting(true);
    try {
      const r = await axios.post(`${API_URL}/api/affiliate/payout/request`, { currency: ccy });
      const method = r.data.method;
      if (method === 'stripe_connect') {
        toast.success(`Instant payout sent: ${r.data.amount} ${r.data.currency} · ref ${r.data.transfer_id?.slice(0, 18)}…`);
      } else {
        toast.success(`Payout of ${r.data.amount} ${r.data.currency} requested — clears within 24h`);
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Payout request failed');
    } finally { setRequesting(false); }
  };

  const balances = data.balance || {};
  const ccysWithBalance = Object.keys(balances).filter(c => balances[c] > 0);
  const thresholds = data.payout_thresholds || {};

  const role = (data.role || 'buyer').toLowerCase();
  const pct = (data.commission_pct ?? 15) / 100;
  const firstName = data.first_name || '';
  const tagline = ROLE_TAGLINE[role] || ROLE_TAGLINE.buyer;
  const products = ROLE_PRODUCTS[role] || ROLE_PRODUCTS.buyer;
  // Cheap derivation — no memo needed (3 items × multiply).
  const earningsPreview = products.map(p => ({ ...p, earn: +(p.price * pct).toFixed(2) }));

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6 mb-8" data-testid={dataTestId}>
      {/* Personalised welcome banner — mirrors the AI Network panel.
          Role-aware tagline + headline commission rate make the offer
          feel concrete to every persona, not a one-size-fits-all card. */}
      <div
        className="bg-gradient-to-br from-[#E07A3A] to-[#9A3412] rounded p-5 mb-5 shadow-md"
        data-testid={`${dataTestId}-welcome`}
      >
        <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/90 mb-1.5 flex items-center gap-1.5">
          <Gift className="h-3.5 w-3.5" />
          {firstName ? `Welcome back, ${firstName}` : 'Welcome back'} · your link earns automatically · {data.commission_pct}% per sale
        </p>
        <p className="font-['Playfair_Display'] text-lg text-white leading-snug" data-testid={`${dataTestId}-tagline`}>
          {tagline}
        </p>
        <div className="inline-flex items-center gap-2 mt-3 bg-black/25 px-3 py-1.5 rounded">
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/90">
            Lifetime earned: {data.lifetime_earned > 0 ? `£${data.lifetime_earned.toFixed(2)}` : '£0.00 — yours to claim'}
          </span>
        </div>
      </div>

      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-bold mb-1">
            Affiliate Programme · {data.commission_pct}% commission ({data.role})
          </p>
          <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A]">Earn from every referral</h3>
        </div>
        <div className="bg-[#0F172A] text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded">
          Lifetime: {data.lifetime_earned > 0 ? `£${data.lifetime_earned.toFixed(2)} earned` : 'New affiliate'}
        </div>
      </div>

      {/* Share link */}
      <div className="bg-[#F8FAFC] border border-slate-200 rounded p-3 mb-4 flex items-center gap-2 flex-wrap">
        <code className="flex-1 min-w-0 text-xs sm:text-sm text-slate-700 font-mono truncate" data-testid={`${dataTestId}-link`}>{shareUrl}</code>
        <Button
          onClick={handleCopy}
          variant="outline"
          size="sm"
          className="text-[10px] uppercase tracking-wider h-8"
          data-testid={`${dataTestId}-copy`}
        >
          {copied ? <Check className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>

      {/* Inline social share */}
      <div className="mb-5">
        <ShareButtons
          url={shareUrl}
          title="Join me on Shopperden — the property platform for the UK/India/Canada market"
          size="sm"
          label="Share with friends"
          dataTestId={`${dataTestId}-share`}
        />
      </div>

      {/* What pays you — role-specific earnings preview.
          Concrete numbers ("you earn £X when a referral buys product Y")
          consistently lift share-rate vs a single headline %. */}
      <div className="mb-5 border border-slate-200 rounded p-4 bg-[#FFFBF5]" data-testid={`${dataTestId}-earnings-preview`}>
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#9A3412] mb-3 flex items-center gap-1.5">
          <TrendingUp className="h-3.5 w-3.5" /> What pays you — at {data.commission_pct}% commission
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {earningsPreview.map((p, i) => (
            <div
              key={p.label}
              className="bg-white border border-slate-200 rounded p-3 hover:border-[#E07A3A] transition-colors"
              data-testid={`${dataTestId}-earn-row-${i}`}
            >
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 truncate">{p.label}</p>
              <p className="text-sm text-slate-700 mt-1">
                £{p.price.toFixed(2)}{p.cadence === '/mo' ? '/mo' : p.cadence === 'each' ? ' each' : ''}
              </p>
              <p className="text-base font-bold text-[#14532D] mt-1">
                + £{p.earn.toFixed(2)}{p.cadence === '/mo' ? '/mo' : ''} to you
              </p>
              <p className="text-[10px] text-slate-400 mt-1 leading-snug">{p.note}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Stripe Connect status — instant payout vs manual */}
      <div className="mb-5 flex items-center gap-3 flex-wrap" data-testid={`${dataTestId}-connect-row`}>
        {connectStatus?.connected && connectStatus?.status === 'verified' ? (
          <div className="flex items-center gap-2 bg-[#F0FDF4] border border-[#BBF7D0] text-[#14532D] text-xs font-semibold px-3 py-2 rounded">
            <Check className="h-3.5 w-3.5" />
            Stripe Connect verified · instant payouts enabled
          </div>
        ) : connectAvailable === false ? (
          // Platform-side Stripe Connect unavailable → never show a button that
          // would bounce. Surface the manual-payout reassurance instead.
          <div
            className="flex items-center gap-2 bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold px-3 py-2 rounded"
            data-testid={`${dataTestId}-manual-payout-badge`}
          >
            <Wallet className="h-3.5 w-3.5 text-slate-500" />
            Manual payouts active — funds clear within 24 hours of approval
          </div>
        ) : connectStatus?.connected ? (
          <>
            <div className="flex items-center gap-2 bg-[#FEF3C7] border border-[#FDE68A] text-[#78350F] text-xs font-semibold px-3 py-2 rounded">
              Onboarding incomplete — finish to enable instant payouts
            </div>
            <Button onClick={handleConnect} disabled={connecting} size="sm" variant="outline" className="text-[10px] uppercase tracking-wider h-8" data-testid={`${dataTestId}-connect-resume`}>
              {connecting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Resume Stripe onboarding'}
            </Button>
          </>
        ) : (
          <>
            <div className="text-xs text-slate-600">Get paid instantly to your bank — connect via Stripe (1-min setup, KYC handled by Stripe).</div>
            <Button onClick={handleConnect} disabled={connecting} size="sm" className="bg-[#635BFF] hover:bg-[#4F47CC] text-white text-[10px] uppercase tracking-wider h-8" data-testid={`${dataTestId}-connect-start`}>
              {connecting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Connect Stripe for instant payouts'}
            </Button>
          </>
        )}
      </div>

      {/* Balances grid (only currencies with positive balance) */}
      {ccysWithBalance.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
          {ccysWithBalance.map(ccy => {
            const bal = balances[ccy];
            const threshold = thresholds[ccy] || 50;
            const ready = bal >= threshold;
            const sym = { GBP: '£', INR: '₹', CAD: 'C$', USD: '$' }[ccy] || '';
            return (
              <div
                key={ccy}
                className={`p-4 rounded border ${ready ? 'bg-[#F0FDF4] border-[#BBF7D0]' : 'bg-[#FEF3C7] border-[#FDE68A]'}`}
                data-testid={`${dataTestId}-balance-${ccy}`}
              >
                <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">{ccy} Balance</p>
                <p className={`text-2xl font-bold mt-1 ${ready ? 'text-[#14532D]' : 'text-[#78350F]'}`}>
                  {ccy === 'INR' ? `${sym}${Math.round(bal).toLocaleString()}` : `${sym}${bal.toFixed(2)}`}
                </p>
                <p className="text-[11px] text-slate-500 mt-1">
                  Threshold: {sym}{ccy === 'INR' ? Math.round(threshold).toLocaleString() : threshold.toFixed(2)}
                </p>
                {ready && (
                  <Button
                    onClick={() => handlePayout(ccy)}
                    disabled={requesting}
                    size="sm"
                    className="w-full mt-3 bg-[#0F172A] hover:bg-[#1E293B] text-white text-[10px] uppercase tracking-wider h-8"
                    data-testid={`${dataTestId}-payout-${ccy}`}
                  >
                    {requesting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Request payout'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded p-5 mb-5 text-center" data-testid={`${dataTestId}-empty`}>
          <Wallet className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-600 font-semibold">No commissions yet</p>
          <p className="text-xs text-slate-400 mt-1">
            Share your link — when a friend signs up and pays, you earn {data.commission_pct}% on every transaction.
          </p>
        </div>
      )}

      {/* Recent credits */}
      {data.recent_credits?.length > 0 && (
        <div className="border-t border-slate-100 pt-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3 flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" /> Recent earnings
          </p>
          <div className="space-y-2">
            {data.recent_credits.map((c, i) => {
              const sym = { GBP: '£', INR: '₹', CAD: 'C$' }[c.currency] || '';
              return (
                <div key={i} className="flex items-center justify-between text-xs py-1.5" data-testid={`${dataTestId}-credit-${i}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-slate-700 truncate">{c.buyer_email} · {(c.purpose || '').replace(/_/g, ' ')}</p>
                    <p className="text-slate-400 text-[10px]">{new Date(c.credited_at).toLocaleDateString()} · {c.commission_pct * 100}% of {sym}{c.amount_paid}</p>
                  </div>
                  <p className="text-emerald-600 font-semibold ml-2 flex-shrink-0">+{sym}{c.commission}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateWidget;
